/**
 * web/src/components/shared/Dot.tsx
 * 10×10 status dot matching `.dot` contract (planning-docs/
 * design_handoff_pixel_dashboard/design_files/styles.css lines 580-604).
 *
 * Pulse halo via the @keyframes pulse-grow defined in index.css; only on
 * running/waiting variants per handoff §Animations + CONTEXT.md "Specifics"
 * (line 165). prefers-reduced-motion is honored via the @media rule in
 * index.css that downgrades pulse-grow to a static low-opacity state
 * (T-05-04-03 mitigation).
 */
import type { CSSProperties, HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/** Status keys understood by Dot. `awaiting` is the teal "waiting on an
 * external party (sponsor reply)" state, distinct from amber `waiting` (a human
 * gate). `future` is an outlined upcoming-step state. */
export type DotStatus =
  | 'running'
  | 'waiting'
  | 'awaiting'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'future';

/**
 * Public props for Dot. Inherits span attributes (so `data-testid`,
 * `aria-label`, etc. forward to the root element) but excludes children
 * since Dot is decorative.
 */
export interface DotProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  status: DotStatus;
  /**
   * When true AND status is 'running', 'waiting', or 'awaiting', renders a
   * pulsing halo via the global @keyframes pulse-grow. Other status values
   * render statically regardless of `pulse` (T-05-04-03 mitigation).
   */
  pulse?: boolean;
  /** Pixel size of the dot (square). Defaults to 10. */
  size?: number;
}

/**
 * Tailwind background utility per status. `future` swaps bg for an outlined
 * border so upcoming steps read as "not yet started".
 */
const statusBg: Record<DotStatus, string> = {
  running: 'bg-running',
  waiting: 'bg-waiting',
  awaiting: 'bg-awaiting',
  completed: 'bg-completed',
  failed: 'bg-failed',
  cancelled: 'bg-cancelled',
  future: 'bg-transparent border-2 border-border-strong box-border',
};

/**
 * Renders a status indicator dot. Decorative — `aria-hidden="true"` is
 * applied by default; consumers can override via the spread props.
 *
 * Side effects: none. Pulse animation references the global
 * `@keyframes pulse-grow` declared in `web/src/index.css` (Plan 01).
 */
export function Dot({ status, pulse = false, size = 10, className, ...rest }: DotProps) {
  const isPulse =
    pulse && (status === 'running' || status === 'waiting' || status === 'awaiting');
  const halo: CSSProperties | undefined = isPulse
    ? {
        position: 'absolute',
        inset: -4,
        borderRadius: '50%',
        background: 'inherit',
        opacity: 0.35,
        animation: 'pulse-grow 1.8s ease-in-out infinite',
        zIndex: -1,
      }
    : undefined;

  return (
    <span
      aria-hidden="true"
      {...rest}
      className={cn(
        'relative inline-block rounded-full flex-shrink-0',
        statusBg[status],
        className,
      )}
      style={{ width: size, height: size, ...rest.style }}
    >
      {isPulse && <span style={halo} />}
    </span>
  );
}
